import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FileCheck,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Ban,
  Upload,
  Download,
  Search,
} from 'lucide-react';
import apiClient from '../services/api';
import { getActiveProfile } from '../utils/connectorConfig';
import useStore from '../store/useStore';
import { formatDateTime } from '../utils/dateFormat';

const PAGE_SIZE = 10;

export default function Contracts() {
  const { connectorKey, addNotification } = useStore();
  const activeProfile = useMemo(() => getActiveProfile(), [connectorKey]);

  const providerUrl = activeProfile?.providerEdcUrl || '';
  const providerApiKey = activeProfile?.providerApiKey || '';
  const consumerUrl = activeProfile?.consumerEdcUrl || '';
  const consumerApiKey = activeProfile?.consumerApiKey || '';

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('active'); // 'active' | 'terminated' | 'all'
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [terminatingId, setTerminatingId] = useState(null);
  const [confirmTerminate, setConfirmTerminate] = useState(null);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const requests = [];
      if (providerUrl) {
        requests.push(
          apiClient
            .post('/edc/provider/contracts', { providerUrl, apiKey: providerApiKey })
            .then(res => (res.data.contracts || []).map(c => ({ ...c, side: 'provider', managementUrl: providerUrl, sideApiKey: providerApiKey })))
            .catch(() => [])
        );
      }
      if (consumerUrl) {
        requests.push(
          apiClient
            .post('/edc/consumer/contracts', { consumerUrl, apiKey: consumerApiKey })
            .then(res => (res.data.contracts || []).map(c => ({ ...c, side: 'consumer', managementUrl: consumerUrl, sideApiKey: consumerApiKey })))
            .catch(() => [])
        );
      }

      if (requests.length === 0) {
        setContracts([]);
        setError('No connector configured. Set up a Provider or Consumer connector first.');
        setLoading(false);
        return;
      }

      const results = await Promise.all(requests);
      setContracts(results.flat());
    } catch (err) {
      console.error('Failed to load contracts:', err);
      setError('Failed to load contracts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [providerUrl, providerApiKey, consumerUrl, consumerApiKey]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  useEffect(() => { setCurrentPage(1); }, [filter, searchQuery]);

  const filteredContracts = useMemo(() => {
    let list = contracts;
    if (filter === 'active') list = list.filter(c => c.terminationStatus === 'ONGOING');
    else if (filter === 'terminated') list = list.filter(c => c.terminationStatus === 'TERMINATED');

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(c =>
        (c.assetTitle || '').toLowerCase().includes(q) ||
        (c.assetId || '').toLowerCase().includes(q) ||
        (c.counterPartyId || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [contracts, filter, searchQuery]);

  const activeCount = contracts.filter(c => c.terminationStatus === 'ONGOING').length;
  const terminatedCount = contracts.filter(c => c.terminationStatus === 'TERMINATED').length;

  const totalPages = Math.max(1, Math.ceil(filteredContracts.length / PAGE_SIZE));
  const paginatedContracts = filteredContracts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleTerminate = async (contract) => {
    const negotiationId = contract.contractNegotiationId;
    if (!negotiationId) {
      addNotification?.({ type: 'error', message: 'Missing negotiation ID for this contract.' });
      setConfirmTerminate(null);
      return;
    }
    setTerminatingId(negotiationId);
    try {
      await apiClient.post('/edc/contracts/terminate', {
        managementUrl: contract.managementUrl,
        apiKey: contract.sideApiKey,
        negotiationId,
        reason: 'Terminated by user via ENACT Dataspace Management Platform',
      });
      addNotification?.({ type: 'success', message: `Contract for "${contract.assetTitle || contract.assetId}" terminated.` });
      setConfirmTerminate(null);
      // Update immediately — EDC processes termination asynchronously, so an
      // immediate re-fetch can still show ONGOING for a moment. Reconcile
      // with the server in the background afterward.
      setContracts(prev => prev.map(c =>
        c.contractNegotiationId === negotiationId ? { ...c, terminationStatus: 'TERMINATED' } : c
      ));
      setTimeout(fetchContracts, 3000);
    } catch (err) {
      console.error('Terminate failed:', err);
      addNotification?.({ type: 'error', message: err.response?.data?.error || 'Failed to terminate contract.' });
    } finally {
      setTerminatingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileCheck className="text-blue-400" size={26} />
            Contracts
          </h1>
          <p className="text-gray-400 text-sm mt-1">Active contract agreements across your connectors, with the ability to terminate them.</p>
        </div>
        <button onClick={fetchContracts} className="btn btn-secondary flex items-center gap-2" disabled={loading}>
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="text-red-400 flex-shrink-0" size={18} />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by asset or counterparty…"
            className="input w-full pl-9"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('active')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === 'active' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
          >
            Active ({activeCount})
          </button>
          <button
            onClick={() => setFilter('terminated')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === 'terminated' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
          >
            Terminated ({terminatedCount})
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
          >
            All ({contracts.length})
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-blue-400" size={32} />
        </div>
      ) : paginatedContracts.length === 0 ? (
        <div className="card text-center py-16">
          <FileCheck className="mx-auto text-gray-600 mb-3" size={40} />
          <p className="text-gray-400">No {filter !== 'all' ? filter : ''} contracts found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {paginatedContracts.map((contract, idx) => {
            const isActive = contract.terminationStatus === 'ONGOING';
            const key = contract.contractNegotiationId || contract.contractAgreementId || idx;
            return (
              <div key={key} className="card space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {contract.side === 'provider'
                      ? <Upload size={16} className="text-blue-400 flex-shrink-0" />
                      : <Download size={16} className="text-purple-400 flex-shrink-0" />}
                    <h3 className="font-semibold truncate" title={contract.assetTitle || contract.assetId}>
                      {contract.assetTitle || contract.assetId || 'Unknown asset'}
                    </h3>
                  </div>
                  <span className={`flex-shrink-0 text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 ${
                    isActive ? 'bg-green-900/20 text-green-400' : 'bg-red-900/20 text-red-400'
                  }`}>
                    {isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
                    {isActive ? 'Active' : 'Terminated'}
                  </span>
                </div>

                <p className="text-xs text-gray-500 uppercase tracking-wide">{contract.side === 'provider' ? 'Providing' : 'Consuming'}</p>

                <div className="text-sm space-y-1">
                  <p className="text-gray-400">
                    Counterparty: <span className="text-gray-200">{contract.counterPartyId || '—'}</span>
                  </p>
                  {contract.contractSigningDate && (
                    <p className="text-gray-400">
                      Signed: <span className="text-gray-200">{formatDateTime(contract.contractSigningDate)}</span>
                    </p>
                  )}
                  <p className="text-gray-400">
                    Transfers: <span className="text-gray-200">{contract.transferProcesses?.length ?? 0}</span>
                  </p>
                </div>

                {isActive && (
                  confirmTerminate === key ? (
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => handleTerminate(contract)}
                        disabled={terminatingId === contract.contractNegotiationId}
                        className="btn flex-1 bg-red-600 hover:bg-red-700 text-white flex items-center justify-center gap-2 text-sm py-1.5"
                      >
                        {terminatingId === contract.contractNegotiationId
                          ? <Loader2 className="animate-spin" size={14} />
                          : <Ban size={14} />}
                        Confirm Terminate
                      </button>
                      <button onClick={() => setConfirmTerminate(null)} className="btn btn-secondary flex-1 text-sm py-1.5">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmTerminate(key)}
                      className="btn btn-secondary w-full flex items-center justify-center gap-2 text-sm py-1.5 text-red-400 hover:text-red-300"
                    >
                      <Ban size={14} />
                      Terminate
                    </button>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 pt-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="btn btn-secondary p-2 disabled:opacity-40"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm text-gray-400">Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="btn btn-secondary p-2 disabled:opacity-40"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
